import { LtSelect, LtSelectOption } from '@/components/LtSelect';
import { useUDT } from '@/infrastructure/hooks/useUDT';
import { customCssClasses, LEAD_CUSTOM_FIELDS } from '@lemontaps/lib/constants';
import { LeadAPI, LeadCustomFieldAPI } from '@lemontaps/lib/types/api';
import { LeadCustomFieldOption, LeadCustomFieldValueType } from '@lemontaps/lib/types/global';
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  IconButton,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import useId from '@mui/material/utils/useId';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { enrichedTextFieldStyles } from '../styles';
import { Close } from '@mui/icons-material';
import { ClearableTextField } from '@/components';
import { Attachments } from './Attachments';
import { getAssetsLocation } from '@/infrastructure/apis/assets';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppTranslation } from '@/infrastructure/hooks/useAppTranslation';
import { Signature } from './Signature';
import { LeadCustomFieldValueAPI } from '@lemontaps/lib/types';
import { downloadSignature } from '@/infrastructure/apis/leadGen';
import { downloadFile } from '@/infrastructure/helper';

type SharedProperties = {
  label?: string;
  value?: LeadCustomFieldValueType | null;
  required?: boolean;
  errorMessage?: string;
  createdOn?: string;
  valueId?: number | null;
  description?: string;
  lead?: Partial<LeadAPI> | null;
};

type TextProps = SharedProperties & {
  onChange: (value: string) => void;
};

const RenderText = ({ onChange, value, label, required, errorMessage }: TextProps) => {
  return (
    <ClearableTextField
      required={required}
      label={label}
      fullWidth
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      error={!!errorMessage}
      helperText={errorMessage}
    />
  );
};

const RenderTextarea = ({ onChange, value, label, errorMessage, required }: TextProps) => {
  return (
    <ClearableTextField
      required={required}
      label={label}
      fullWidth
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      multiline
      rows={4}
      error={!!errorMessage}
      helperText={errorMessage}
    />
  );
};

type AttachmentsProps = SharedProperties & {
  onChange: (value: string[]) => void;
};

const RenderAttachments = ({
  onChange,
  value: _value,
  label,
  required,
  errorMessage,
}: AttachmentsProps) => {
  const labelId = useId();
  const { getAccessTokenSilently } = useAuth0();
  const stringifiedValue = JSON.stringify(_value || []);
  const assetIds = useMemo(() => JSON.parse(stringifiedValue) as string[], [stringifiedValue]);

  const [assetsLocation, setAssetsLocation] = useState<{ id: string; location: string }[]>([]);

  useEffect(() => {
    const fetchAssetsLocation = async () => {
      if (!assetIds.length) {
        setAssetsLocation([]);
        return;
      }
      const response = await getAssetsLocation(getAccessTokenSilently, assetIds);
      setAssetsLocation(response);
    };
    fetchAssetsLocation();
  }, [assetIds, getAccessTokenSilently]);

  const handleChange = (value: { id: string; location: string }[]) => {
    onChange(value.map(asset => asset.id));
  };

  return (
    <FormControl fullWidth aria-describedby={labelId} required={required} error={!!errorMessage}>
      <FormLabel id={labelId} error={!!errorMessage}>
        {label}
      </FormLabel>
      <Attachments value={assetsLocation} onChange={handleChange} />
      <FormHelperText sx={{ mt: 1 }} error={!!errorMessage}>
        {errorMessage}
      </FormHelperText>
    </FormControl>
  );
};

type SignatureProps = SharedProperties & {
  onChange: (value: string) => void;
};

const RenderSignature = ({
  onChange,
  value: _value,
  label,
  required,
  errorMessage,
  valueId,
  description,
  createdOn: _createdOn,
  lead,
}: SignatureProps) => {
  const labelId = useId();
  const { getAccessTokenSilently } = useAuth0();
  const assetId = _value as string | null;

  const [asset, setAsset] = useState<{ id: string; location: string } | null>(null);

  useEffect(() => {
    const fetchAssetsLocation = async () => {
      if (!assetId) {
        setAsset(null);
        return;
      }
      const response = await getAssetsLocation(getAccessTokenSilently, [assetId]);
      setAsset({ id: assetId, location: response?.[0]?.location || '' });
    };
    fetchAssetsLocation();
  }, [assetId, getAccessTokenSilently]);

  const handleChange = (asset: { id: string; location: string } | null) => {
    onChange(asset?.id || '');
  };

  const handleDownload = async () => {
    if (!valueId) return;
    try {
      const name =
        ['signature', lead?.firstName, lead?.lastName].filter(Boolean).join('_') || 'signature';
      const response = await downloadSignature(getAccessTokenSilently, valueId);
      downloadFile(URL.createObjectURL(new Blob([response.data])), `${name}.pdf`);
    } catch (error) {
      console.error('Error downloading signature:', error);
    }
  };
  const createdOn = _createdOn ? moment(_createdOn).format('DD/MM/YYYY HH:mm') : '';

  return (
    <FormControl fullWidth aria-describedby={labelId} required={required} error={!!errorMessage}>
      <Signature
        title={label}
        labelElement={
          <FormLabel id={labelId} error={!!errorMessage}>
            {label}
          </FormLabel>
        }
        description={description}
        value={asset}
        onChange={handleChange}
        onDownload={handleDownload}
        createdOn={createdOn}
        canDownload={Boolean(valueId)}
      />
      <FormHelperText sx={{ mt: 1 }} error={!!errorMessage}>
        {errorMessage}
      </FormHelperText>
    </FormControl>
  );
};

type DropdownProps = SharedProperties & {
  onChange: (value: string) => void;
  options?: LeadCustomFieldOption[];
};

const RenderDropDown = ({
  onChange,
  value: _value,
  label,
  options = [],
  required,
  errorMessage,
}: DropdownProps) => {
  const value = _value as string;
  const labelId = useId();

  return (
    <FormControl fullWidth aria-describedby={labelId} required={required} error={!!errorMessage}>
      <InputLabel id={labelId} error={!!errorMessage}>
        {label}
      </InputLabel>
      <LtSelect labelId={labelId} label={label} value={value} onChange={onChange} clearable>
        {options.map(option => (
          <LtSelectOption key={option.id} value={option.id}>
            {option.title}
          </LtSelectOption>
        ))}
      </LtSelect>
      {errorMessage && <FormHelperText error={!!errorMessage}>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};

type RadioProps = SharedProperties & {
  onChange: (value: string | number) => void;
  options?: LeadCustomFieldOption[];
};

const RenderRadio = ({
  onChange,
  value: _value,
  options = [],
  label,
  required,
  errorMessage,
}: RadioProps) => {
  const value = _value as string | number;

  const handleChange = (val: string | number) => onChange(val);

  const labelId = useId();

  return (
    <FormControl fullWidth aria-describedby={labelId} required={required} error={!!errorMessage}>
      <FormLabel id={labelId} error={!!errorMessage}>
        {label}
      </FormLabel>
      <RadioGroup value={value} onChange={({ target: { value } }) => handleChange(value)}>
        {options.map(option => (
          <FormControlLabel
            key={option.id}
            value={option.id}
            label={option.title}
            control={<Radio />}
          />
        ))}
      </RadioGroup>
      {errorMessage && <FormHelperText error={!!errorMessage}>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};

type CheckboxProps = SharedProperties & {
  onChange: (value: (string | number)[]) => void;
  options?: LeadCustomFieldOption[];
};

const RenderCheckbox = ({
  onChange,
  value: _value,
  options = [],
  errorMessage,
  label,
  required,
}: CheckboxProps) => {
  const value = _value as (string | number)[];

  const handleChange = (option: { id: string | number; title: string }) => {
    const currentValues = value || [];
    if (currentValues.includes(option.id)) {
      onChange(currentValues.filter(val => val !== option.id));
    } else {
      onChange([...currentValues, option.id]);
    }
  };

  const labelId = useId();
  return (
    <FormControl fullWidth aria-describedby={labelId} required={required} error={!!errorMessage}>
      <FormLabel id={labelId} error={!!errorMessage}>
        {label}
      </FormLabel>
      <FormGroup>
        {options.map(option => (
          <FormControlLabel
            key={option.id}
            label={option.title}
            control={
              <Checkbox
                checked={(value || []).includes(option.id)}
                onChange={() => handleChange(option)}
              />
            }
          />
        ))}
      </FormGroup>
      {errorMessage && <FormHelperText error={!!errorMessage}>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};

type MultiSelectProps = SharedProperties & {
  onChange: (value: (string | number)[]) => void;
  options?: LeadCustomFieldOption[];
};

const RenderMultiSelect = ({
  onChange,
  value: _value,
  options = [],
  label,
  required,
  errorMessage,
}: MultiSelectProps) => {
  const value = _value as (string | number)[];

  const autoCompleteValue = options.filter(option => value?.includes(option.id));

  return (
    <Autocomplete
      multiple
      options={options}
      getOptionLabel={option => option.title}
      value={autoCompleteValue}
      onChange={(_, value) => onChange(value.map(option => option.id))}
      filterSelectedOptions
      disableClearable
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={!!errorMessage}
          helperText={errorMessage}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {autoCompleteValue.length > 0 && (
                  <IconButton sx={{ mr: -0.8 }} onClick={() => onChange?.([])}>
                    <Close fontSize='small' />
                  </IconButton>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

type SingleCheckboxProps = SharedProperties & {
  onChange: (value: boolean) => void;
};

const RenderCheckboxSingle = ({
  onChange,
  value: _value,
  label,
  required,
  errorMessage,
}: SingleCheckboxProps) => {
  const value = _value as boolean;

  return (
    <FormControl fullWidth required={required} error={!!errorMessage}>
      <FormControlLabel
        color={errorMessage ? 'error' : undefined}
        label={label}
        control={
          <Checkbox
            color={errorMessage ? 'error' : undefined}
            checked={value}
            onChange={() => onChange(!value)}
          />
        }
      />
      {errorMessage && <FormHelperText error={!!errorMessage}>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};

type DateProps = SharedProperties & {
  onChange: (value: string) => void;
};

const RenderDate = ({ onChange, value: _value, label, required, errorMessage }: DateProps) => {
  const formatDate = (date: string) => {
    if (!date) return '';
    return moment(new Date(date)).format().substring(0, 10);
  };

  const value = formatDate(_value as string);

  return (
    <ClearableTextField
      InputLabelProps={{ shrink: true }}
      value={value ?? ''}
      type='date'
      label={label}
      onChange={e => onChange(formatDate(e.target.value))}
      fullWidth
      required={required}
      error={!!errorMessage}
      helperText={errorMessage}
    />
  );
};

const RenderDateTime = ({ onChange, value: _value, label, required, errorMessage }: DateProps) => {
  const formatDate = (date: string) => {
    if (!date) return '';
    return moment(new Date(date as string)).format('YYYY-MM-DDTHH:mm:ss');
  };

  const value = formatDate(_value as string);

  return (
    <ClearableTextField
      InputLabelProps={{ shrink: true }}
      value={value ?? ''}
      type='datetime-local'
      label={label}
      onChange={e => onChange(formatDate(e.target.value))}
      fullWidth
      required={required}
      error={!!errorMessage}
      helperText={errorMessage}
    />
  );
};

type NumberProps = SharedProperties & {
  onChange: (value: number) => void;
};
const RenderNumber = ({ onChange, value: _value, label, required, errorMessage }: NumberProps) => {
  const value = _value?.toString();

  return (
    <ClearableTextField
      required={required}
      label={label}
      type='number'
      fullWidth
      value={value ?? ''}
      onChange={e => onChange(parseFloat(e.target.value))}
      error={!!errorMessage}
      helperText={errorMessage}
    />
  );
};

const getLabel = (
  field: LeadCustomFieldAPI,
  currentLanguage: string,
  fallbackLanguage: string | null | undefined,
): string => {
  const currentLanguageLabel = currentLanguage ? field.translatedName?.[currentLanguage] : null;
  const fallbackLanguageLabel = fallbackLanguage ? field.translatedName?.[fallbackLanguage] : null;

  const label = currentLanguageLabel || fallbackLanguageLabel || field.name;
  return label;
};

const getDescription = (
  field: LeadCustomFieldAPI,
  currentLanguage: string,
  fallbackLanguage: string | null | undefined,
): string => {
  const currentLanguageDescription = currentLanguage
    ? field.translatedDescription?.[currentLanguage]
    : null;
  const fallbackLanguageDescription = fallbackLanguage
    ? field.translatedDescription?.[fallbackLanguage]
    : null;

  const description =
    currentLanguageDescription || fallbackLanguageDescription || field.description;
  return description || '';
};

const getOptions = (
  field: LeadCustomFieldAPI,
  currentLanguage: string,
  fallbackLanguage: string | null | undefined,
): LeadCustomFieldOption[] => {
  const currentLanguageOptions = field.translatedMeta?.[currentLanguage]?.options || [];
  const fallbackLanguageOptions = fallbackLanguage
    ? field.translatedMeta?.[fallbackLanguage]?.options || []
    : [];

  const options = currentLanguageOptions
    .map(option => (option.title ? option : fallbackLanguageOptions.find(o => o.id === option.id)))
    .filter(Boolean) as LeadCustomFieldOption[];

  return options.length ? options : field.meta?.options || [];
};

type SingleCustomFieldRendererProps = {
  fieldType: LeadCustomFieldAPI;
  onChange: (value: LeadCustomFieldValueType, optionTypeId: number) => void;
  required?: boolean;
  errorMessage?: string;
  isFilledByAi?: boolean;
  leadCustomFieldValue?: LeadCustomFieldValueAPI;
  lead?: Partial<LeadAPI> | null;
};
export const SingleCustomFieldRenderer = ({
  leadCustomFieldValue,
  fieldType,
  onChange,
  required,
  errorMessage,
  isFilledByAi,
  lead,
}: SingleCustomFieldRendererProps) => {
  const _value = leadCustomFieldValue?.value;
  const value = typeof _value === 'undefined' ? null : _value;
  const { activeLanguage } = useAppTranslation();
  const currentLanguage = activeLanguage;
  const { fallbackLanguage } = useUDT();

  /**Find Label */
  const label = getLabel(fieldType, currentLanguage, fallbackLanguage);

  /**Find options */
  const options = getOptions(fieldType, currentLanguage, fallbackLanguage);

  /**Find Description */
  const description = getDescription(fieldType, currentLanguage, fallbackLanguage);

  let Component = null;
  switch (fieldType.type) {
    case LEAD_CUSTOM_FIELDS.TEXT:
      Component = RenderText;
      break;
    case LEAD_CUSTOM_FIELDS.TEXTAREA:
      Component = RenderTextarea;
      break;
    case LEAD_CUSTOM_FIELDS.DROPDOWN:
      Component = RenderDropDown;
      break;
    case LEAD_CUSTOM_FIELDS.CHECKBOX:
      Component = RenderCheckbox;
      break;
    case LEAD_CUSTOM_FIELDS.RADIO:
      Component = RenderRadio;
      break;
    case LEAD_CUSTOM_FIELDS.MULTI_SELECT:
      Component = RenderMultiSelect;
      break;
    case LEAD_CUSTOM_FIELDS.DATE:
      Component = RenderDate;
      break;
    case LEAD_CUSTOM_FIELDS.DATETIME:
      Component = RenderDateTime;
      break;
    case LEAD_CUSTOM_FIELDS.CHECKBOX_SINGLE:
      Component = RenderCheckboxSingle;
      break;
    case LEAD_CUSTOM_FIELDS.NUMBER:
      Component = RenderNumber;
      break;
    case LEAD_CUSTOM_FIELDS.ATTACHMENTS:
      Component = RenderAttachments;
      break;
    case LEAD_CUSTOM_FIELDS.SIGNATURE:
      Component = RenderSignature;
      break;
  }

  const [isMultiLineLabel, setIsMultiLineLabel] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!boxRef.current) return;
    const boxElement = boxRef.current;
    const label = boxElement.querySelector('.LtInputLabel-root');
    if (!label) return;
    if (label.clientHeight > 24) setIsMultiLineLabel(true);
  }, [boxRef]);

  if (!Component) return null;

  return (
    <Box
      ref={boxRef}
      className={customCssClasses.contactFormBox.customField.className}
      sx={{
        '.LtInputLabel-root': {
          whiteSpace: 'normal',
          position: isMultiLineLabel ? 'static' : undefined,
          transform: isMultiLineLabel ? 'none' : undefined,
          maxWidth: isMultiLineLabel ? '100%' : undefined,
          marginBottom: isMultiLineLabel ? 1 : undefined,
        },
        legend: {
          display: isMultiLineLabel ? 'none' : undefined,
        },
        fieldset: {
          top: isMultiLineLabel ? 0 : undefined,
        },
        ...(isFilledByAi ? enrichedTextFieldStyles : {}),
      }}
    >
      <Component
        options={options}
        value={value}
        onChange={value => onChange(value, fieldType.id)}
        required={required}
        label={label}
        errorMessage={errorMessage}
        createdOn={leadCustomFieldValue?.createdOn}
        valueId={leadCustomFieldValue?.id}
        description={description}
        lead={lead}
      />
    </Box>
  );
};
